import React, { useState, useRef, useEffect, useCallback } from 'react';
import cn from 'classnames';
import debounce from 'lodash/debounce';

import clamp from 'js/utils/clamp';
import rangeMap from 'js/utils/rangeMap';

import Button from 'components/button/Button';
import TextInput from 'components/form-elements/text-input/TextInput';

import { IRangeSlider } from 'source/NFR.types';

interface RangeSliderState {
  draggingFrom: boolean;
  draggingTo: boolean;
  from: number;
  screenLeft: number;
  to: number;
  width: number;
}

interface RangeSliderProps extends IRangeSlider {
  className?: string;
}

const RangeSlider: React.FC<RangeSliderProps> = ({
  className,
  from: fromProps,
  label,
  max,
  min,
  submit,
  to: toProps
}) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const draggingFromRef = useRef(false);
  const draggingToRef = useRef(false);

  const [state, setState] = useState<RangeSliderState>(() => ({
    draggingFrom: false,
    draggingTo: false,
    from: parseInt(fromProps.value as string) || min,
    screenLeft: 0,
    to: parseInt(toProps.value as string) || max,
    width: 0
  }));

  const measure = useCallback(() => {
    if (!sliderRef.current) {
      return;
    }

    setState(prevState => ({
      ...prevState,
      width: sliderRef.current!.offsetWidth,
      screenLeft: sliderRef.current!.getBoundingClientRect().left
    }));
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onResize = useCallback(debounce(measure, 100), [measure]);

  const clampFrom = useCallback(
    (value: number, currentState: RangeSliderState): number => {
      return clamp(value, min, currentState.to - 1);
    },
    [min]
  );

  const clampTo = useCallback(
    (value: number, currentState: RangeSliderState): number => {
      return clamp(value, currentState.from + 1, max);
    },
    [max]
  );

  const setFrom = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setState(state => ({
        ...state,
        from: clampFrom(parseInt(e.target.value, 10), state)
      }));
    },
    [clampFrom]
  );

  const setTo = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setState(state => ({
        ...state,
        to: clampTo(parseInt(e.target.value, 10), state)
      }));
    },
    [clampTo]
  );

  const onDrag = useCallback(
    (xPosition: number) => {
      if (!draggingFromRef.current && !draggingToRef.current) {
        return;
      }

      setState(state => {
        const position = xPosition - state.screenLeft;
        const value = Math.round(rangeMap(position, 0, state.width, min, max));

        if (state.draggingFrom) {
          return { ...state, from: clampFrom(value, state) };
        }

        if (state.draggingTo) {
          return { ...state, to: clampTo(value, state) };
        }

        return state;
      });
    },
    [min, max, clampFrom, clampTo]
  );

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      onDrag(e.clientX);
    },
    [onDrag]
  );

  const onTouchMove = useCallback(
    (e: TouchEvent) => {
      onDrag(e.touches[0].clientX);
    },
    [onDrag]
  );

  const onFromMouseDown = useCallback(() => {
    draggingFromRef.current = true;
    setState(state => ({ ...state, draggingFrom: true, draggingTo: false }));
  }, []);

  const onToMouseDown = useCallback(() => {
    draggingToRef.current = true;
    setState(state => ({ ...state, draggingFrom: false, draggingTo: true }));
  }, []);

  const resetDragging = useCallback(() => {
    draggingFromRef.current = false;
    draggingToRef.current = false;
  }, []);

  useEffect(() => {
    window.addEventListener('resize', onResize);
    window.addEventListener('mouseup', resetDragging);
    window.addEventListener('touchend', resetDragging);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('touchmove', onTouchMove);
    measure();

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('mouseup', resetDragging);
      window.removeEventListener('touchend', resetDragging);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onTouchMove);
      onResize.cancel();
    };
  }, [onResize, resetDragging, onMouseMove, onTouchMove, measure]);

  const { from, to } = state;

  // Positions in percent
  const fromPosition = ((from - min) / (max - min)) * 100;
  const toPosition = ((to - min) / (max - min)) * 100;

  return (
    <fieldset className={cn('range-slider', className)}>
      <legend>{label}</legend>

      <div className="range-slider--inputs">
        <TextInput
          controlled={true}
          onChange={setFrom}
          type="number"
          theme="small"
          {...fromProps}
          value={String(state.from)}
        />
        <TextInput
          controlled={true}
          onChange={setTo}
          type="number"
          theme="small"
          {...toProps}
          value={String(state.to)}
        />
      </div>

      <div className="range-slider--slider" ref={sliderRef}>
        <div className="range-slider--slider-inner">
          {state.width > 0 && (
            <>
              <div
                className="range-slider--active-region"
                style={{
                  left: `${fromPosition}%`,
                  width: `${toPosition - fromPosition}%`
                }}
              />
              <div
                className="range-slider--knob range-slider--knob-from"
                onMouseDown={onFromMouseDown}
                onTouchStart={onFromMouseDown}
                style={{
                  left: `${fromPosition}%`,
                  zIndex: state.draggingFrom ? 2 : undefined
                }}
              />
              <div
                className="range-slider--knob range-slider--knob-to"
                onMouseDown={onToMouseDown}
                onTouchStart={onToMouseDown}
                style={{
                  left: `${toPosition}%`,
                  zIndex: state.draggingTo ? 2 : undefined
                }}
              />
            </>
          )}
        </div>
      </div>

      <Button className="range-slider--submit" type="submit" theme="primary" {...submit} />
    </fieldset>
  );
};

RangeSlider.displayName = 'RangeSlider';

export default RangeSlider;
